import React, { useMemo, useRef } from 'react';
import classes from '../AnswersBlock.module.scss';
import classnames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import _ from 'lodash';
import { connect } from 'react-redux';

import { toggleAudio } from 'actions/audioActions';
import { imagesURL } from 'config';


const INITIAL_DELAY = 1000;

const getDelays = (num, speed) =>
  _.shuffle(
    _.range(INITIAL_DELAY, (speed / 2) * num + INITIAL_DELAY, speed / 2)
  );

const StopItAnswers = ({
  audio,
  useImages,
  longAnswers,
  question,
  isResolved,
  isMultiplayer,
  animationSpeed,
  animation,
  coloredAnswerBoxes,
  contentPath,
  colors,
  textStyle,
  clickHandler,
  withAudio,
  visibleAfterResolvedTime,
  forceCorrectAnswerVisible = false,
  mobileInteraction = false,
  mobileSelectedAnswerId = null,
  isAndroidMobile = false,
  workAreaStyle,
}) => {
  const answersDelays = useMemo(
    () => getDelays(question.answers.length, animationSpeed),
    [question, animationSpeed]
  );
  const mobileSelectionRects = useRef({});
  // Keep the original animate.css behavior except in native mobile WebViews
  // reporting reduced motion: animate.css disables .animated with !important.
  const restoreNativeMobileTransitions = mobileInteraction
    && typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion)').matches;

  return (
    <div
      className={classnames('moving-answers-work-area', {
        'moving-answers-work-area--four-answers': question.answers.length >= 4,
        [classes.imagesAnswersBlock]: useImages,
        [classes.textAnswersBlock]: !useImages && !longAnswers,
        [classes.longAnswersBlock]: longAnswers,
        [classes.multiplayer]: isMultiplayer,
      })}
      style={workAreaStyle}
    >
      {question.answers.map((answer, index) => {
        const revealCorrectAnswer = forceCorrectAnswerVisible && answer.correct;
        const keepMobileSelection = mobileInteraction && !!mobileSelectedAnswerId;
        const isMobileSelected = keepMobileSelection && answer.id === mobileSelectedAnswerId;
        const isMobileHidden = keepMobileSelection && !isMobileSelected;
        const mobileSelectionRect = isMobileSelected
          ? mobileSelectionRects.current[answer.id]
          : null;
        const animationDuration = !isResolved
          ? animationSpeed
          : animationSpeed / 2;

        const animationDelay = !isResolved
          ? answersDelays[index]
          : answer.correct && !question.fillIn
          ? visibleAfterResolvedTime * 1000 - 1
          : 0;

        const answerPostition = {
          top: (index + 1) % 2 === 0 ? 0 : 'auto',
          bottom: (index + 1) % 2 === 0 ? 'auto' : 0,
          left:
            (100 / question.answers.length) * index +
            100 / (question.answers.length * 2) +
            '%',
        };

        return (
          <CSSTransition
            appear
            unmountOnExit
            mountOnEnter
            in={!isResolved || revealCorrectAnswer || keepMobileSelection}
            key={answer.id}
            timeout={animationDuration + animationDelay}
            classNames={{
              appearActive: animation.in,
              exitActive: animation.out,
            }}
          >
            {(state) => (
              <div
                className={classnames({
                  // The existing animate.css mobile rule for reduced motion
                  // cancels all StopIt entrance keyframes via !important.
                  // Keep the authored directional classes on native mobile;
                  // desktop retains the original global animated class.
                  animated: !restoreNativeMobileTransitions,
                  [classes.imageAnswerWrapper]: useImages,
                  [classes.textAnswerWrapper]: !useImages,
                  'moving-answers-answer--selected': isMobileSelected,
                  'moving-answers-answer--correct-reveal':
                    revealCorrectAnswer && mobileInteraction,
                  'moving-answers-answer--hidden-stop-it': isMobileHidden,
                  'moving-answers-answer--selected-android': isMobileSelected && isAndroidMobile,
                })}
                style={revealCorrectAnswer
                  ? {
                      left: '50%',
                      right: 'auto',
                      top: '50%',
                      bottom: 'auto',
                      transform: 'translate(-50%, -50%)',
                      animation: 'none',
                      zIndex: 50,
                    }
                  : mobileSelectionRect
                  ? {
                      left: mobileSelectionRect.left,
                      right: 'auto',
                      top: mobileSelectionRect.top,
                      bottom: 'auto',
                      width: mobileSelectionRect.width,
                      height: mobileSelectionRect.height,
                      transform: 'none',
                      animation: 'none',
                      animationPlayState: 'paused',
                      zIndex: 60,
                    }
                  : {
                      ...answerPostition,
                      animationDuration: animationDuration / 1000 + 's',
                      animationDelay: animationDelay / 1000 + 's',
                      // .animated normally provides this. Restore the same
                      // final frame when native mobile omits that class.
                      animationFillMode: restoreNativeMobileTransitions ? 'both' : undefined,
                      animationPlayState: undefined,
                      zIndex: isMobileSelected ? 60 : undefined,
                    }}
              >
                <button
                  className={classnames(classes.answerBtn, 'moving-answers-answer-button', {
                    [classes.fadeIn]: state === 'entering' && !revealCorrectAnswer,
                    [classes.fadeOut]: state === 'exiting' && !revealCorrectAnswer,
                  })}
                  disabled={isResolved || state === 'entering' || keepMobileSelection}
                  style={{
                    ...textStyle,
                    ...(coloredAnswerBoxes ? colors[index] : {}),
                    ...(revealCorrectAnswer
                      ? { transform: 'none', animation: 'none', opacity: 1 }
                      : {}),
                  }}
                  onTouchEnd={mobileInteraction
                    ? (event) => {
                        event.preventDefault();
                        event.stopPropagation();

                        const workArea = event.currentTarget.closest(
                          '.moving-answers-work-area'
                        );
                        const answerWrapper = event.currentTarget.parentElement;
                        const answerRect =
                          answerWrapper && answerWrapper.getBoundingClientRect();
                        const workAreaRect = workArea && workArea.getBoundingClientRect();

                        if (answerRect && workAreaRect) {
                          mobileSelectionRects.current[answer.id] = {
                            left: answerRect.left - workAreaRect.left,
                            top: answerRect.top - workAreaRect.top,
                            width: answerRect.width,
                            height: answerRect.height,
                          };
                        }

                        clickHandler(answer);
                      }
                    : undefined}
                  onClick={mobileInteraction ? undefined : clickHandler.bind(null, answer)}
                  aria-pressed={isMobileSelected}
                >
                  {useImages ? (
                    <img src={contentPath + answer.image} alt="" />
                  ) : (
                    answer.text
                  )}
                </button>

                {withAudio && answer.audio && (
                  <button
                    className={classnames(
                      classes.playAudioBtn,
                      'moving-answers-answer-audio',
                      {
                        'moving-answers-answer-audio--before':
                          index >= Math.ceil(question.answers.length / 2),
                        'moving-answers-answer-audio--after':
                          index < Math.ceil(question.answers.length / 2),
                        [classes.fadeIn]: state === 'entering',
                        [classes.fadeOut]: state === 'exiting',
                      }
                    )}
                    disabled={isResolved || state === 'entering' || keepMobileSelection}
                    onClick={() => toggleAudio(contentPath + answer.audio)}
                  >
                    <img
                      src={`${imagesURL}/Images/Audio/${
                        audio.audioSrc === contentPath + answer.audio &&
                        audio.isPlaying
                          ? 'speak_stop'
                          : 'speak_enabled'
                      }.png`}
                      alt=""
                    />
                  </button>
                )}
              </div>
            )}
          </CSSTransition>
        );
      })}
    </div>
  );
};

const mapStateToProps = ({ audio }) => ({ audio });

export default connect(mapStateToProps)(StopItAnswers);
